import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NbDialogRef, NbDialogService } from '@nebular/theme';
import { DeveloperClientsService } from 'src/app/services/developer-clients.service';
import { ApiService } from 'src/app/services/api.service';
import { ApiUrl } from 'src/app/services/apiUrl';
import { ChainsService } from 'src/app/services/chains.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { SecretDisplayDialogComponent } from '../secret-display-dialog/secret-display-dialog.component';
import { FONIX_SENSE_PRESET_SCOPES, scopeLabelKey } from '../oauth-scope-labels';
import Swal from 'sweetalert2';

type AccessMode = 'stores' | 'chain';

@Component({
  selector: 'app-developer-client-form',
  templateUrl: './developer-client-form.component.html',
  styleUrls: ['./developer-client-form.component.scss']
})
export class DeveloperClientFormComponent implements OnInit {
  @Input() clientId: string = '';

  clientForm: FormGroup;
  availableScopes: string[] = [];
  stores: any[] = [];
  chains: any[] = [];
  selectedScopes: string[] = [];
  selectedStoreIds: string[] = [];
  accessMode: AccessMode = 'stores';
  isEditMode = false;
  role: string = localStorage.getItem('role') || '';
  scopeLabelKey = scopeLabelKey;
  readonly customersPiiScope = 'customers:pii';

  get canUseChainMode(): boolean {
    return this.role === 'admin' || this.role === 'chain_manager';
  }

  constructor(
    private dialogRef: NbDialogRef<DeveloperClientFormComponent>,
    private devClientsService: DeveloperClientsService,
    private apiService: ApiService,
    private chainsService: ChainsService,
    private dialogService: NbDialogService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {
    this.clientForm = new FormGroup({
      name: new FormControl('', Validators.required),
      description: new FormControl(''),
      chainId: new FormControl(null),
    });
  }

  ngOnInit(): void {
    this.isEditMode = !!this.clientId;
    this.loadScopes();
    this.loadStores();
    if (this.canUseChainMode) {
      this.loadChains();
    }
    if (this.isEditMode) {
      this.loadClient();
    }
  }

  loadScopes(): void {
    this.devClientsService.getScopes().subscribe({
      next: (res: any) => {
        this.availableScopes = res.response?.data?.scopes || res.scopes || [];
      },
      error: () => {}
    });
  }

  loadStores(): void {
    const managerId = localStorage.getItem('managerId');
    let endpoint = ApiUrl.storeListAdminDropdown;
    if (this.role === 'manager' && managerId) {
      endpoint = `${ApiUrl.storeListManagerDropdown}${managerId}`;
    }

    this.apiService.getRequest(endpoint).subscribe({
      next: (res: any) => {
        const responseData = res.response || res;
        const raw =
          this.role === 'manager'
            ? responseData.data || []
            : responseData.data?.storesData || responseData.data || [];
        this.stores = (raw || []).map((s: any) => ({
          ...s,
          storeName: s.storeName || s.name,
        }));
      },
      error: () => {}
    });
  }

  loadChains(): void {
    const req =
      this.role === 'chain_manager'
        ? this.chainsService.getMyChains()
        : this.chainsService.getChains();

    req.subscribe({
      next: (res: any) => {
        const raw =
          res.chains ||
          res.response?.data?.chains ||
          res.response?.data ||
          res ||
          [];
        this.chains = Array.isArray(raw) ? raw : [];
      },
      error: () => {}
    });
  }

  loadClient(): void {
    this.spinner.show();
    this.devClientsService.getClient(this.clientId).subscribe({
      next: (res: any) => {
        const client = res.response?.data || res;
        this.clientForm.patchValue({
          name: client.name,
          description: client.description || '',
          chainId: client.chainId?._id || client.chainId || null,
        });
        this.selectedScopes = [...(client.scopes || [])];

        if (client.chainId) {
          this.accessMode = 'chain';
        } else {
          this.accessMode = 'stores';
          const ids =
            (client.storeIds || []).map((s: any) => s._id || s).filter(Boolean) ||
            (client.storeId?._id ? [client.storeId._id] : client.storeId ? [client.storeId] : []);
          this.selectedStoreIds = ids;
        }
        this.spinner.hide();
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire(
          'Error',
          err.error?.response?.message || this.translate.instant('DEV_CLIENTS.LOAD_ONE_ERROR'),
          'error'
        );
        this.dialogRef.close();
      }
    });
  }

  setAccessMode(mode: AccessMode): void {
    if (!this.canUseChainMode && mode === 'chain') {
      return;
    }
    this.accessMode = mode;
    if (mode === 'chain') {
      this.selectedStoreIds = [];
    } else {
      this.clientForm.get('chainId')?.setValue(null);
    }
  }

  onStoreIdsChange(ids: string[]): void {
    this.selectedStoreIds = ids || [];
  }

  toggleScope(scope: string): void {
    const idx = this.selectedScopes.indexOf(scope);
    if (idx > -1) {
      this.selectedScopes.splice(idx, 1);
    } else {
      this.selectedScopes.push(scope);
    }
  }

  isScopeSelected(scope: string): boolean {
    return this.selectedScopes.includes(scope);
  }

  applyFonixPreset(): void {
    this.selectedScopes = [...FONIX_SENSE_PRESET_SCOPES];
  }

  chainDisplayName(chain: any): string {
    return chain.chainName || chain.name || chain._id;
  }

  storeDisplayName(store: any): string {
    return store.storeName || store.name || store._id;
  }

  private buildPayload(): any {
    const payload: any = {
      name: this.clientForm.get('name')?.value,
      description: this.clientForm.get('description')?.value || '',
      scopes: this.selectedScopes,
    };

    if (this.accessMode === 'chain') {
      payload.chainId = this.clientForm.get('chainId')?.value;
      payload.storeIds = [];
    } else {
      payload.chainId = null;
      payload.storeIds = this.selectedStoreIds;
    }
    return payload;
  }

  private validateForm(): boolean {
    if (this.clientForm.invalid) {
      this.toastr.error(this.translate.instant('DEV_CLIENTS.VALIDATION_REQUIRED'));
      return false;
    }
    if (this.selectedScopes.length === 0) {
      this.toastr.error(this.translate.instant('DEV_CLIENTS.VALIDATION_SCOPES'));
      return false;
    }
    if (this.accessMode === 'chain') {
      if (!this.clientForm.get('chainId')?.value) {
        this.toastr.error(this.translate.instant('DEV_CLIENTS.VALIDATION_CHAIN'));
        return false;
      }
    } else if (!this.selectedStoreIds.length) {
      this.toastr.error(this.translate.instant('DEV_CLIENTS.VALIDATION_STORES'));
      return false;
    }
    return true;
  }

  onSubmit(): void {
    if (!this.validateForm()) {
      return;
    }

    const data = this.buildPayload();
    this.spinner.show();

    const req = this.isEditMode
      ? this.devClientsService.updateClient(this.clientId, data)
      : this.devClientsService.createClient(data);

    req.subscribe({
      next: (res: any) => {
        this.spinner.hide();
        const body = res.response?.data || res;
        this.dialogRef.close({ saved: true });

        if (!this.isEditMode) {
          this.dialogService.open(SecretDisplayDialogComponent, {
            context: {
              clientId: body.clientId,
              clientSecret: body.client_secret,
              titleKey: 'DEV_CLIENTS.SECRET_DIALOG.CREATED_TITLE',
            }
          });
        } else {
          this.toastr.success(this.translate.instant('DEV_CLIENTS.UPDATED'));
        }
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire(
          'Error',
          err.error?.response?.message ||
            this.translate.instant(this.isEditMode ? 'DEV_CLIENTS.UPDATE_ERROR' : 'DEV_CLIENTS.CREATE_ERROR'),
          'error'
        );
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
